import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** Pages that have an admin-controllable hero. */
export type HeroPageKey = "home" | "cars" | "about" | "contact";

/** Simplified hero media type — either a still image or a video.
 *  Video URLs can be either a direct .mp4/.webm or a YouTube watch link;
 *  the renderer auto-detects YouTube. */
export type HeroMediaType = "image" | "video";

export interface HeroConfig {
  mediaType: HeroMediaType;
  /** Image URL, direct video URL (.mp4/.webm) or YouTube watch URL. */
  url: string;
}

/** Settings key in `site_settings` for a given page. */
export function heroSettingsKey(page: HeroPageKey): string {
  return `hero_${page}`;
}

/** Default hero video used across all pages when no override is set. */
export const DEFAULT_HERO_VIDEO_URL = "https://www.youtube.com/watch?v=P6FLqCB1BgY";

/** Code-default fallback for each page. All pages share the same default
 *  video — admins only override media when they want something page-specific. */
export const HERO_DEFAULTS: Record<HeroPageKey, HeroConfig> = {
  home: { mediaType: "video", url: DEFAULT_HERO_VIDEO_URL },
  cars: { mediaType: "video", url: DEFAULT_HERO_VIDEO_URL },
  about: { mediaType: "video", url: DEFAULT_HERO_VIDEO_URL },
  contact: { mediaType: "video", url: DEFAULT_HERO_VIDEO_URL },
};

/** Merge a partial DB value with the page defaults. Empty strings count as
 *  "not set" so admins can clear a field to revert to the default. Older
 *  rows may contain extra keys (eyebrow, title, overlay…) — those are
 *  ignored now that the editor is media-only. */
export function mergeHeroConfig(
  page: HeroPageKey,
  raw: Partial<HeroConfig> | null | undefined,
): HeroConfig {
  const base = HERO_DEFAULTS[page];
  if (!raw) return base;
  return {
    mediaType: raw.mediaType ?? base.mediaType,
    url: raw.url && raw.url.trim() !== "" ? raw.url : base.url,
  };
}

/** Fetch the hero config for a page from `site_settings`, falling back to the
 *  code defaults. The home page is back-compat with the legacy `hero` key. */
export function useHeroSettings(page: HeroPageKey) {
  const key = heroSettingsKey(page);
  return useQuery({
    queryKey: ["hero-settings", page],
    queryFn: async (): Promise<HeroConfig> => {
      const keys = page === "home" ? [key, "hero"] : [key];
      const { data } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", keys);
      const row = data?.find((r) => r.key === key) ?? data?.find((r) => r.key === "hero");
      const value = row?.value as (Partial<HeroConfig> & { type?: string }) | null;
      // Legacy schema used `{ type, url }` and the older "youtube" media type
      // — normalise both into the new image/video model.
      const normalised: Partial<HeroConfig> | null = value
        ? {
            url: value.url,
            mediaType: ((): HeroMediaType | undefined => {
              const m = value.mediaType ?? (value.type as string | undefined);
              if (m === "image") return "image";
              if (m === "video" || m === "youtube") return "video";
              return undefined;
            })(),
          }
        : null;
      return mergeHeroConfig(page, normalised);
    },
  });
}
